import { generatePath, useHistory, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

import { WorkspaceDomainClient } from 'api/clients/WorkspaceDomainClient'
import { pathAfterLogin } from 'utils/urlUtils'
import { useDefaultWorkspacePath } from 'utils/useDefaultWorkspacePath'
import { UserService } from 'api/clients/UserService'
import { Auth, AuthWithHeaders, Workspace } from 'types'
import { CREATE_WORKSPACE_PATH, RECENT_DOCS_PATH } from 'paths'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { useQueryParams } from 'hooks/useQueryParams'
import { findPrivateWorkspaces, removeWorkspaceInviteToken, isAlmanacCoreWorkspace } from 'utils/workspaceUtils'
import { getWorkspaceOrigin, workspaceAfterLogin } from 'utils/workspaceDomainUtils'
import { trackRegistrationEnd, RegistrationType } from '../utils'
import { useOnboardingLoader } from 'hooks/useOnboardingLoader'
import { reportError } from 'utils/error'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { UserClient } from 'api/clients/UserClient'
import { authMerge } from 'redux/actions'
import { useIframeWrapper } from 'hooks/useIframeWrapper'
import {
  AUTH_TOKEN_PARAM,
  FORCE_DEFAULT_WORKSPACE_PARAM,
  REDIRECT_URL_PARAM,
  REGISTRATION_TYPE_PARAM,
  WORKSPACE_INVITE_TOKEN_PARAM,
} from '../constants'
import { isCertified } from 'api/clients/WorkspaceDomainClientUtils'
import * as HandbookClient from 'api/clients/HandbookClient'
import { ArticleClient } from 'api/clients/ArticleClient'
import { metaFromPath } from 'utils/metaFromPath'
import { useQueryClient } from 'react-query'
import { isUndefined } from 'lodash'
import { useRootStore } from 'stores/RootStore'
import { useAuthChannel } from 'providers/AuthChannelProvider/AuthChannelProvider'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const useLoginOrSignUp = () => {
  const history = useHistory()
  const queryParams = useQueryParams()
  const { guest, domain: customDomain, templateId, aspectRatioKey } = queryParams
  const queryClient = useQueryClient()
  const forceDefaultWorkspace = queryParams[FORCE_DEFAULT_WORKSPACE_PARAM] === 'true'
  const { showOnboardingLoader, hideOnboardingLoaderAfterTimeout } = useOnboardingLoader()
  const dispatch = useDispatch()
  const { defaultWorkspacePath } = useDefaultWorkspacePath()
  const isWrapperTest = useIframeWrapper()
  const location = useLocation()
  const featureFlags = useRootStore().featureFlags
  const { refreshAllTabs } = useAuthChannel()
  const { isMobile, isIOSNative } = useResponsiveDesign()

  const framedRedirectTo = (path: string) => {
    if (window.top && window.top !== window && !isWrapperTest) {
      window.top.location.assign(path)
    } else {
      history.push(path)
    }
  }

  const performRedirect = async (auth: Auth | null, redirectTo: string, registrationType?: RegistrationType) => {
    if (!auth?.signed_up && registrationType) trackRegistrationEnd(registrationType)
    await completeSignup(auth)
    framedRedirectTo(redirectTo)
  }

  const completeSignup = async (auth?: Auth | null) => {
    if (!auth || auth.signed_up) return

    const res = await UserClient.completeSignup()

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      dispatch(
        authMerge({
          auth: {
            ...auth,
            signed_up: true,
          },
        }),
      )
    }
  }

  const seedWorkspace = async (workspace: Workspace | undefined) => {
    if (!workspace) return

    const res = await WorkspaceClient.seed(workspace.key)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      const { onboarding_file_path: onboardingFilePath } = res.data
      if (onboardingFilePath) return onboardingFilePath
    } else {
      // Don't report 4xx errors
      if ('status' in res && res.status < 500) return

      reportError('Error seeding workspace', {
        severity: 'error',
        metaData: {
          status: 'status' in res && res.status,
          message: 'message' in res && res.message,
          workspace_id: workspace.id,
        },
      })
    }
  }

  /**
   * Handle navigating to a workspace via login or signup flows
   * - signup ensures user registration type is tracked and signup is completed.
   *
   * @param workspace
   * @param auth
   * @param registrationType
   * @param redirectTo
   * @param onboardingDocPath
   * @param recentRedirect
   * @param needsOnboarding
   * @returns undefined - redirects to `redirectTo` path, `onboardingDocPath`, or the default workspace's path
   */
  const goToWorkspace = async (
    workspace: Workspace | undefined,
    auth: Auth | null,
    options: {
      registrationType?: RegistrationType
      redirectTo?: string
      onboardingDocPath?: string
      recentRedirect?: boolean
      withLoader?: boolean
      promotionCode?: string
    } = {},
  ) => {
    if (!workspace) return

    if (isUndefined(options.withLoader) || options.withLoader) showOnboardingLoader({ timeout: 3000 })

    UserService.setCurrentWorkspaceId(workspace.id)

    // track registration type and complete signup
    if (!auth?.signed_up) {
      if (!isAlmanacCoreWorkspace(workspace.id)) {
        if (options.registrationType) trackRegistrationEnd(options.registrationType)
        completeSignup(auth)
      } else {
        // We should never redirect a fresh new user to core. They should create a workspace!
        reportError('Redirecting new user to home', {
          severity: 'error',
          metaData: {
            auth,
            redirectTo: options.redirectTo,
            registrationType: options.registrationType,
          },
        })
      }
    }

    queryClient.invalidateQueries(['user-preferences', workspace.id])

    let redirect
    const isAPMobileWeb = !isIOSNative && isMobile && workspace.is_virtual_marketer

    if (options.redirectTo) {
      // Custom redirect
      redirect = options.redirectTo
    } else if (options.recentRedirect) {
      // redirect to recent
      redirect = generatePath(RECENT_DOCS_PATH, { workspaceId: workspace.id })
    } else if (options.onboardingDocPath) {
      // Onboarding doc
      redirect = options.onboardingDocPath
    } else if (workspace) {
      redirect = defaultWorkspacePath({
        id: workspace.id,
        guestReader: workspace.guest,
        homepageDocSlug: workspace.homepage_doc_slug,
        promotionCode: options.promotionCode,
        search: templateId && aspectRatioKey ? `templateId=${templateId}&aspectRatioKey=${aspectRatioKey}` : '',
        goToCalendar: isAPMobileWeb,
      })
    } else {
      redirect = CREATE_WORKSPACE_PATH
    }

    framedRedirectTo(redirect)
  }

  const onLoginOrSignUpSuccess = async (
    auth: AuthWithHeaders,
    registrationType: RegistrationType,
    redirectTo?: string,
    workspaceInviteToken?: string,
    promotionCode?: string,
  ) => {
    refreshAllTabs()
    UserService.setAuthCookie(auth)
    featureFlags.setAuth(auth)

    // Check if user is ICP-blocked and should be redirected to the signup ICP block
    const icpBlocked = localStorage.getItem('icp_blocked')
    if (icpBlocked) {
      window.location.href = `/signup?businessType=${encodeURIComponent(icpBlocked)}` // eslint-disable-line no-restricted-properties
      return
    }

    // This is the only case in which we don't fetch or seed a workspace
    if (guest && redirectTo) {
      return performRedirect(auth, redirectTo, registrationType)
    }

    // fetch and set all workspaces associated with a user.
    const workspacesResp = await client.getUserWorkspaces()
    if (workspacesResp.type !== SUCCESS_RESPONSE_TYPE) {
      reportError('Failed to load workspaces when registering user.', {
        severity: 'error',
        metaData: { auth },
      })
      showToastMessage('There was a problem loading your workspaces. Our team has been notified about it.', 'error')
      return
    }
    const { workspaces } = workspacesResp.data
    UserService.setWorkspaces(workspaces)

    const privateWorkspaces = findPrivateWorkspaces(workspaces)

    if (!customDomain) {
      // If the user doesn't have any workspace at all, make them go through the create workspace flow.
      if (!privateWorkspaces.length) return 'CREATE_WORKSPACE'

      if (privateWorkspaces.length === 1 && !auth.signed_up && privateWorkspaces[0].admin) {
        // User did not complete registration
        UserService.setCurrentWorkspaceId(privateWorkspaces[0].id)
        return 'COMPANY_DATA'
      }
    }

    const pathMeta = redirectTo ? metaFromPath(redirectTo) : undefined
    let pathInferedWorkspaceId: number | undefined = pathMeta?.workspaceId

    // TODO: HandbookClient.getFolderWorkspace was made public and can be called before this step
    if (!pathInferedWorkspaceId && pathMeta?.handbookKey) {
      const folderRes = await HandbookClient.getHandbookWorkspace(pathMeta.handbookKey)
      if (folderRes.type === SUCCESS_RESPONSE_TYPE) {
        pathInferedWorkspaceId = folderRes.data.company.id
      }
    }

    // TODO: ArticleClient.getWorkspace was made public and can be called before this step
    if (!pathInferedWorkspaceId && pathMeta?.articleKey) {
      const articleRes = await ArticleClient.getWorkspace(pathMeta.articleKey)
      if (articleRes.type === SUCCESS_RESPONSE_TYPE) {
        pathInferedWorkspaceId = articleRes.data.company.id
      }
    }

    const invitedWorkspace =
      workspaceInviteToken?.length && workspaces.find((ws: Workspace) => ws.invitation_token === workspaceInviteToken)
    const lastWorkspace = workspaceAfterLogin({
      workspaces,
      customDomain,
      forceDefaultWorkspace,
      currentWorkspaceId: pathInferedWorkspaceId || auth.last_workspace_id || auth.company_id,
    })
    const nonGuestWorkspace = privateWorkspaces.filter((ws) => !ws.guest)?.[0]
    const landingWorkspace = invitedWorkspace || lastWorkspace || nonGuestWorkspace

    // Attempt to prevent a redirect into a custom domain when we just asked to remain at almanac.io
    if (redirectTo && forceDefaultWorkspace && pathInferedWorkspaceId) {
      redirectTo = undefined
    }

    // Any customDomain that does not match this is not even viable for a direct login into
    const workspaceDomain =
      lastWorkspace != null &&
      lastWorkspace.workspace_domain != null &&
      !lastWorkspace.workspace_domain.subdomain &&
      isCertified(lastWorkspace.workspace_domain)

    // Handle custom domain redirect path.
    if (workspaceDomain && !forceDefaultWorkspace) {
      const domain = getWorkspaceOrigin(lastWorkspace?.workspace_domain)
      const redirectPath = pathAfterLogin(history, {
        ...auth,
        redirect_to:
          redirectTo || (landingWorkspace && generatePath(RECENT_DOCS_PATH, { workspaceId: landingWorkspace.id })),
      })

      const searchParams = new URLSearchParams(location.search)
      searchParams.delete('email')
      searchParams.delete('domain')
      searchParams.delete('workspaceName')
      searchParams.delete('utm_source')
      searchParams.delete('utm_medium')
      searchParams.delete('utm_campaign')
      searchParams.delete('inviter')
      searchParams.delete('registration_verification_token')
      searchParams.delete(WORKSPACE_INVITE_TOKEN_PARAM)
      searchParams.delete(REGISTRATION_TYPE_PARAM)
      searchParams.delete(REDIRECT_URL_PARAM)
      searchParams.delete(AUTH_TOKEN_PARAM)

      WorkspaceDomainClient.createCookie(domain, auth, redirectPath, searchParams)

      // createCookie will handle redirects
      return
    }

    // If the user has a landing workspace, redirect them to that workspace
    if (landingWorkspace) {
      if (invitedWorkspace) removeWorkspaceInviteToken()
      return goToWorkspace(landingWorkspace, auth, { registrationType, redirectTo, promotionCode })
    }

    return goToWorkspace(privateWorkspaces[0], auth, { registrationType, redirectTo, promotionCode })
  }

  return {
    completeSignup,
    goToWorkspace,
    onLoginOrSignUpSuccess,
    performRedirect,
    showOnboardingLoader,
    hideOnboardingLoaderAfterTimeout,
    seedWorkspace,
  }
}

export default useLoginOrSignUp
